import { changeQuery, isModulesPage, parseUrlSearchParams } from '@/libs/url';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import qs from 'querystring';
import { useHistory } from '@jupiter/plugin-runtime/router';
import {
  AttachmentDetail,
  ContentsType,
  CourseContent,
  CourseDetail,
  GetCourseDetailData,
  KnowledgeDetail,
  LearningInfo,
  RoleType,
  SellerLearningCenterApi,
  sellerLearningCenterApiClient,
} from '@/api/seller/serv/oec_seller_learning_center_api';
import { getQuery } from '@i18n-ecom/lib/dist/commonjs/url';
import { CODE_SUCCESS } from '@features/constants/network';
import { useTranslation } from '@jupiter-app/plugin-i18n';
import { findContentId, findPath } from '@/hooks/useFetchContents';
import { useContextUser } from '@features/hooks';
import { ErrorCode } from '../essay-detail/constants';
import { useUserModel } from '@/models';
import { PagePathV2 } from '@/types/index';
import { getPopReportQuery } from '@/hooks/usePopQuery';

export interface ErrorText {
  title: string;
  text: string;
  buttonText: string;
  buttonClick?: () => void;
}

export default function useCourseSeries() {
  const history = useHistory();
  const { selectedKey, setSelectedKey, defaultOpenKeys, setDefaultOpenKeys } =
    useMenuData();
  const { t } = useTranslation();
  const { i18n } = useTranslation();

  const [user] = useContextUser();
  const { isLogin, isSeller, isCreator } = useUserModel();
  const currentLanguage = useRef(i18n.language);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [empty, setEmpty] = useState(false);
  const [needLogin, setNeedLogin] = useState(false);

  const [reportObj, setReportObj] = useState<object>({});
  const [courseData, setCourseData] = useState<CourseDetail>();
  const [courseInfo, setCourseInfo] = useState<
    LearningInfo & Pick<GetCourseDetailData, 'prev_course' | 'next_course'>
  >();
  const [courseType, setCourseType] = useState(0);
  const [courseDetail, setCourseDetail] = useState<AttachmentDetail>();
  const [essayDetail, setEssayDetail] = useState<KnowledgeDetail>();
  const [courseName, setCourseName] = useState<string>();
  const courseCategoryRef = useRef<ContentsType | undefined>(undefined);

  const [errorText, setErrorText] = useState<ErrorText>({
    title: '',
    text: '',
    buttonText: '',
  });
  const learningId = getQuery(location.search, 'learning_id')?.toString();
  const course_type = getQuery(location.search, 'course_type')?.toString();
  const contentId = getQuery(location.search, 'content_id')?.toString() ?? '';

  const categoryMap: Record<ContentsType, string> = {
    [ContentsType.Faq_Details]: 'faq_details',
    [ContentsType.Policy_Center]: 'policy_center',
    [ContentsType.Feature_Guide]: 'feature_guide',
    [ContentsType.Courses]: 'courses',
    [ContentsType.Agreement_Center]: 'agreement_center',
  };

  const updateReportObj = (obj: typeof reportObj) => {
    const { channel, ...search } = getQuery(location.search);

    if (channel) {
      Object.assign(obj, { channel });

      history.replace({ search: qs.stringify(search as any) });
    }

    setReportObj(obj);
  };

  const fetchCourseData = async () => {
    if (!learningId) {
      setError(true);
      setLoading(false);
      return;
    }
    setNeedLogin(false);
    setLoading(true);
    const req = { course_id: Number(learningId) };
    if (course_type) {
      req['course_type'] = course_type;
    }
    const res = await sellerLearningCenterApiClient.GetCourseDetail(req);
    if (res.code === CODE_SUCCESS && res.data) {
      const data = res.data;
      setError(false);
      setEmpty(false);
      setNeedLogin(false);
      setCourseData(data?.course_detail);
      setCourseInfo(
        Object.assign(
          {
            prev_course: data?.prev_course,
            next_course: data?.next_course,
          },
          data?.course_detail?.info,
        ),
      );
      courseCategoryRef.current = data.category;
    } else if (
      res.code === ErrorCode.NoContent ||
      res.code === ErrorCode.ContentNeedSync ||
      res.code === ErrorCode.ContentMapNotRight
    ) {
      setEmpty(true);
      setError(false);
      setNeedLogin(false);
    } else if (res.code === ErrorCode.ContentOnlyForLogin) {
      setNeedLogin(true);
      setEmpty(false);
    } else {
      setErrorText({
        title: t('seller_university_load_fail_page_title'),
        text: t('seller_university_load_fail_page_content'),
        buttonText: t('seller_university_load_fail_page_btn'),
        buttonClick: fetchCourseData,
      });
      setError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (isLogin === -1) {
      return;
    }
    if (i18n.language !== currentLanguage.current) {
      setCourseData(undefined);
      currentLanguage.current = i18n.language;
    }
    fetchCourseData();
  }, [i18n.language, learningId, contentId, isLogin]);

  // const { KBRenderError, viewLoaded } = useKBRender();

  const renderCourseDetail = (urlContentId: string | number) => {
    const { id, path } = getIdAndPath(courseData?.course!, urlContentId);
    setSelectedKey(Number(id));
    setDefaultOpenKeys(path);
    if (id !== urlContentId) {
      history.replace({
        pathname: PagePathV2.CourseDetail,
        search: changeQuery({ learning_id: learningId || '', content_id: id }),
      });
    }
    const { contentId, type, attachment, knowledge, name } = findInfo(
      courseData?.course,
      Number(id),
    );

    // when click menuItem, reFind the path
    if (type === 3 && courseData?.course?.length) {
      const queryList = parseUrlSearchParams();
      updateReportObj({
        page_name: 'content_detail',
        type: 'page',
        content_type: 'course',
        content_module: isModulesPage()
          ? 'modules'
          : courseCategoryRef.current
            ? categoryMap[courseCategoryRef.current]
            : 'courses',
        course_name: name,
        learning_id: queryList['learning_id'] || learningId,
        is_login: user.user_id ? '1' : '0',
        prev_page_name: window.document.referrer || '',
        content_id: queryList['content_id'] || contentId,
        content_name: name,
        url: window.location.href,
        article_id: contentId,
        article_name: knowledge.knowledge_name,
        is_video: false,
        identity_type: isCreator ? 'creator' : isSeller ? 'seller' : '',
        ...getPopReportQuery(),
      });
    }
    setCourseName(name);
    setCourseType(type);
    setCourseDetail(attachment);
    setEssayDetail(knowledge);
  };

  useEffect(() => {
    if (courseData?.course) {
      renderCourseDetail(contentId);
    }
  }, [courseData?.course, contentId]);

  return {
    learningId,
    error,
    loading,
    empty,
    needLogin,
    courseData,
    errorText,
    selectedKey,
    defaultOpenKeys,
    fetchCourseData,
    courseName,
    courseType,
    essayDetail,
    courseDetail,
    courseInfo,
    // KBRenderError,
    // viewLoaded,
    renderCourseDetail,
    reportObj,
  };
}

// function useKBRender() {
//   const [KBRenderError, setKBRenderError] = useState(false);
//   const [viewLoaded, setViewLoaded] = useState(false);

//   useEffect(() => {
//     async function getKBRenderReact() {
//       if (typeof window.KBRenderReact === 'function') {
//         await window.KBRenderReact(React, ReactDOM);
//       }
//       if (!window.KBRenderReact || !window.KBRenderReact.KRender) {
//         setKBRenderError(true);
//       }
//       setViewLoaded(true);
//     }
//     getKBRenderReact();
//   }, []);

//   return {
//     KBRenderError,
//     viewLoaded,
//   };
// }

function useMenuData() {
  const [selectedKey, setSelectedKey] = useState<number>();
  const [defaultOpenKeys, setDefaultOpenKeys] = useState<string[]>();
  return {
    selectedKey,
    setSelectedKey,
    defaultOpenKeys,
    setDefaultOpenKeys,
  };
}

function findInfo(
  contents: Array<CourseContent> | undefined,
  targetId: number,
) {
  if (!contents?.length) {
    return;
  }
  for (const item of contents) {
    if (item.content_id === targetId) {
      return {
        contentId: item?.content_id,
        name: item?.content_name,
        type: item?.type,
        attachment: item?.attachment,
        knowledge: item?.knowledge,
      };
    }
    if (item.sub_contents?.length) {
      const res = findInfo(item.sub_contents, targetId);
      if (res) {
        return res;
      }
    }
  }
}

//   /** 1目录 2附件 3文章 */

function getIdAndPath(courseTree: CourseContent[], urlId) {
  const firstContentId = findContentId(courseTree[0])?.contentId;
  const firstContentPath = findPath(courseTree, firstContentId).map(content =>
    String(content.content_id),
  );
  const urlContentPath = findPath(courseTree, Number(urlId)).map(content =>
    String(content?.content_id),
  );

  if (urlContentPath?.length > 0) {
    return { id: urlId, path: urlContentPath };
  } else {
    return { id: firstContentId, path: firstContentPath };
  }
}
